/**
 * Plans shared by link, on the device (`D273`) — PURE.
 *
 * - The SENDING end: the words the share sheet carries — the code always,
 *   and the link only while `D257`'s switch names a base
 *   ({@link shareMessage}).
 * - The RECEIVING end: `?plan=` read once at boot ({@link landingPlan}),
 *   taken out of the address ({@link withoutPlan}) and kept on the device
 *   for {@link PENDING_PLAN_TTL_MS} ({@link livePendingPlan}), until the
 *   person is signed in and the app opens the preview.
 * - The preview's lines, in words ({@link lineSummary}).
 *
 * Opening a link imports nothing and generates nothing (`T-145`): the id
 * opens a preview that only reads.
 */
import {
  formatPlanShareCode,
  PLAN_SHARE_PARAM,
  type PlanShareId,
  planShareId,
  planShareLinkFor,
  type SharedPlanLine,
} from "@repo/contract/community";

/** A link opened more than a month ago is not what the person came for. */
export const PENDING_PLAN_TTL_MS = 30 * 86_400_000;

/** The day's letter, as the plan names its days: A, B, C… */
export function dayLetter(index: number): string {
  return String.fromCharCode(65 + (index % 26));
}

/** What the share sheet carries: the code, and the link once links exist. */
export function shareMessage(params: {
  readonly base: string | null;
  readonly ref: string | null;
  readonly shareId: string;
  readonly withCode: (code: string) => string;
  readonly withLink: (link: string, code: string) => string;
}): string {
  const code = formatPlanShareCode(params.shareId);
  if (params.base === null) {
    return params.withCode(code);
  }
  return params.withLink(
    planShareLinkFor(params.base, params.shareId, params.ref),
    code
  );
}

/** `?plan=` of a landing URL's search, when it is a share id. */
export function landingPlan(search: string): PlanShareId | null {
  const value = new URLSearchParams(search).get(PLAN_SHARE_PARAM);
  if (value === null) {
    return null;
  }
  const parsed = planShareId.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** The search with `plan` taken out, so the router never sees it. */
export function withoutPlan(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(PLAN_SHARE_PARAM);
  const rest = params.toString();
  return rest === "" ? "" : `?${rest}`;
}

/** The kept landing, if well formed and recent. */
export function livePendingPlan(
  raw: string | null,
  now: number
): PlanShareId | null {
  if (raw === null) {
    return null;
  }
  try {
    const value = JSON.parse(raw) as { at?: unknown; shareId?: unknown };
    const parsed = planShareId.safeParse(value.shareId);
    if (
      !parsed.success ||
      typeof value.at !== "number" ||
      now - value.at > PENDING_PLAN_TTL_MS ||
      value.at > now
    ) {
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

export interface LineWords {
  readonly hold: (seconds: number) => string;
  readonly rest: (seconds: number) => string;
}

/** `3 × 8–12 · descanso 90 s`, `3 × 45 s`, or as much of it as is known. */
export function lineSummary(line: SharedPlanLine, words: LineWords): string {
  const reps = repsOf(line, words);
  const dose =
    line.sets === null
      ? reps
      : `${line.sets} × ${reps === "" ? "…" : reps}`.trim();
  const parts = [dose];
  if (line.restSeconds !== null && line.restSeconds > 0) {
    parts.push(words.rest(line.restSeconds));
  }
  return parts.filter((part) => part !== "").join(" · ");
}

function repsOf(line: SharedPlanLine, words: LineWords): string {
  if (line.durationSeconds !== null) {
    return words.hold(line.durationSeconds);
  }
  if (line.repsMin !== null && line.repsMax !== null) {
    return line.repsMin === line.repsMax
      ? String(line.repsMin)
      : `${line.repsMin}–${line.repsMax}`;
  }
  const one = line.repsMin ?? line.repsMax;
  return one === null ? "" : String(one);
}
